// frontend/src/widgets/WorkPackageProgressWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-07
// Updated:      2026-10-16 11:30 BST
//
// Description: Tenant-scoped Work Package operations and monthly completion overview.
//              Task Lists launch the shared TaskListExecutionModal through TaskModalHost.
// Change Log:
//   2026-10-07 15:18 BST: Initial theme-aware/mobile-friendly implementation.
//   2026-10-16 11:30 BST: Added active WP portfolio, blocker visibility, shared Task List
//       execution launcher, month-of-completion type dials and mobile-first tab layout.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const label = (s) => String(s || "pending").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const dateLabel = (s) => s ? new Date(s).toLocaleDateString("en-GB") : "Not scheduled";
const statusPill = (status) => {
  const key = String(status || "pending").toLowerCase();
  const danger = ["blocked", "failed", "waiting"].includes(key);
  const success = key === "completed";
  const rgb = danger ? [220,38,38] : success ? [22,163,74] : [249,115,22];
  return {
    display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999,
    padding: "5px 10px", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap",
    background: `rgba(${rgb.join(",")},.12)`, color: danger ? "#991b1b" : success ? "#166534" : "#9a3412",
    border: `1px solid rgba(${rgb.join(",")},.22)`,
  };
};
const openList = (row, wp) => window.dispatchEvent(new CustomEvent("vision:open-task-list", {
  detail: { taskListId: row.id, jobId: wp.job_id, workPackageId: wp.id },
}));
const openTask = (task, wp) => window.dispatchEvent(new CustomEvent("vision:open-task", {
  detail: { jobId: wp.job_id, workPackageId: wp.id, taskId: task.id },
}));
const initialMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function WorkPackageProgressWidget({ jobId, workPackageId }) {
  const { theme } = useTheme();
  const [month, setMonth] = useState(initialMonth);
  const [tab, setTab] = useState("active");
  const [payload, setPayload] = useState({ items: [], by_type: [] });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/work-packages/progress-overview", {
        baseURL: "/api", params: { month },
      });
      setPayload(data || { items: [], by_type: [] });
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unable to load Work Package progress.");
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => {
    load();
    window.addEventListener("vision:tasks-changed", load);
    window.addEventListener("dashboard:refresh", load);
    return () => {
      window.removeEventListener("vision:tasks-changed", load);
      window.removeEventListener("dashboard:refresh", load);
    };
  }, [load]);
  const items = useMemo(() => (payload.items || []).filter((wp) => {
    if (jobId && Number(wp.job_id) !== Number(jobId)) return false;
    if (workPackageId && Number(wp.id) !== Number(workPackageId)) return false;
    if (typeFilter && wp.package_type !== typeFilter) return false;
    return showCompleted || !wp.completed_at;
  }), [payload, jobId, workPackageId, typeFilter, showCompleted]);
  const metrics = payload.by_type || [];
  const maxCompleted = Math.max(1, ...metrics.map((m) => Number(m.completed_this_month || 0)));
  const accent = theme?.accent_color || "#f97316";
  return (
    <div className="h-full min-h-0 w-full overflow-auto rounded-lg bg-white text-gray-900 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <strong className="text-sm font-bold">Work Package Progress</strong>
        <button type="button" onClick={load} className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white px-3 py-2 rounded text-xs">Refresh</button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <label className="flex items-center gap-2 text-xs font-semibold">
          Month
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="min-h-11 rounded-lg border px-2 py-1 text-gray-900 bg-white" />
        </label>
        <div className="grid grid-cols-2 gap-1 flex-1" role="tablist" aria-label="Work Package views">
          {[{ id:"active", text:"Active Work" },{ id:"types", text:"By Type" }].map((t) => (
            <button type="button" key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`${tab === t.id ? "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick" : "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick"} text-white px-3 py-2 rounded min-h-11 text-xs font-bold`}>
              {t.text}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="p-3 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm">{String(error)}</div>}
      {loading && <div className="text-sm text-gray-500 p-3">Loading Work Packages…</div>}
      {!loading && !error && tab === "types" && (
        <>
          <p className="text-xs text-gray-500">Completed during {month}, regardless of original scheduling date. Dials show counts, not completion rates.</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {metrics.map((metric) => {
              const count = Number(metric.completed_this_month || 0);
              const circumference = 2 * Math.PI * 37;
              return (
                <button type="button" key={metric.type} onClick={() => { setTypeFilter(metric.type); setShowCompleted(true); setTab("active"); }}
                  className="rounded-xl border border-gray-200 p-3 text-gray-900 bg-white hover:border-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 text-center space-y-1">
                  <svg viewBox="0 0 100 100" className="w-24 h-24 mx-auto" aria-label={`${count} ${metric.type} Work Packages completed`}>
                    <circle cx="50" cy="50" r="37" stroke="#e5e7eb" strokeWidth="9" fill="none" />
                    <circle cx="50" cy="50" r="37" stroke={accent} strokeWidth="9" fill="none" strokeLinecap="round"
                      strokeDasharray={`${circumference * count / maxCompleted} ${circumference}`} transform="rotate(-90 50 50)" />
                    <text x="50" y="56" textAnchor="middle" fontSize="24" fontWeight="800" fill="#111827">{count}</text>
                  </svg>
                  <div className="text-sm font-bold break-words">{metric.type}</div>
                  <div className="text-xs text-gray-500">Completed this month</div>
                  <div className="text-xs text-gray-500">{metric.active} active · {metric.blocked} blocked</div>
                </button>
              );
            })}
          </div>
          {!metrics.length && <div className="text-sm text-gray-500 p-3">No Work Package activity found.</div>}
        </>
      )}
      {!loading && !error && tab === "active" && (
        <>
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <select aria-label="Filter by Work Package type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="min-h-11 min-w-0 rounded-lg border bg-white text-gray-900 px-2 text-sm">
              <option value="">All Work Package types</option>
              {metrics.map((m) => <option key={m.type} value={m.type}>{m.type}</option>)}
            </select>
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} /> Include completed this month</label>
          </div>
          <div className="space-y-3">
            {items.map((wp) => {
              const activeLists = (wp.task_lists || []).filter((list) => list.status !== "completed");
              const standalone = (wp.standalone_tasks || []).filter((task) => task.status !== "completed" && task.status !== "cancelled");
              return (
                <div key={wp.id} className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0"><div className="font-bold text-sm break-words">{wp.title}</div><div className="text-xs text-gray-500">{wp.job_ref} · {wp.package_ref} · {wp.package_type}</div></div>
                    <span style={statusPill(wp.blocked_count ? "blocked" : wp.completed_at ? "completed" : wp.status)}><span>●</span>{wp.blocked_count ? `${wp.blocked_count} blocked` : label(wp.completed_at ? "completed" : wp.status)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-semibold"><span>{wp.completed_tasks} / {wp.total_tasks} tasks completed</span><span>{wp.progress_pct}%</span></div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width:`${wp.progress_pct}%`,background:accent}} /></div>
                  <div className="text-xs text-gray-500">{wp.completed_at ? `Completed ${dateLabel(wp.completed_at)}` : `Planned ${dateLabel(wp.planned_start)}`} · {wp.resource_ids?.length ? `WP resources: ${wp.resource_ids.join(", ")}` : "No WP-level resource"}</div>
                  <div className="space-y-1">
                    {activeLists.map((list) => (
                      <button type="button" key={list.id} onClick={() => openList(list,wp)} className="w-full flex gap-2 items-center justify-between text-left min-h-11 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 py-2 text-gray-900">
                        <span className="min-w-0"><span className="text-sm font-semibold break-words">{list.name} ({list.total} tasks)</span><span className="block text-xs text-gray-500">{list.completed}/{list.total} completed · {list.resource_ids?.length ? `Assigned: ${list.resource_ids.join(", ")}` : "Unassigned"}</span></span>
                        <span style={statusPill(list.status)}>{label(list.status)}</span>
                      </button>
                    ))}
                    {standalone.map((task) => (
                      <button type="button" key={task.id} onClick={() => openTask(task,wp)} className="w-full flex gap-2 items-center justify-between text-left min-h-11 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 py-2 text-gray-900">
                        <span className="text-sm font-semibold break-words">{task.title}</span><span style={statusPill(task.blocked ? "blocked" : task.status)}>{label(task.blocked ? "blocked" : task.status)}</span>
                      </button>
                    ))}
                    {!activeLists.length && !standalone.length && <div className="text-xs text-gray-500 py-1">No outstanding executable tasks.</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {!items.length && <div className="text-sm text-gray-500 p-3">No Work Packages match this view.</div>}
        </>
      )}
    </div>
  );
}
